// Gera um date sem o timezone.
export const generateDateUTC = () => {
    const now = Date.UTC(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate(),
        new Date().getHours(),
        new Date().getMinutes(),
        new Date().getSeconds()
    );

    const formattedDate = new Date(now).toISOString();

    return formattedDate;
}

// INFO(Devs): Formato exigido pelo input HTML datetime-local (sem timezone).
export const formatDateTimeLocalInput = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hour}:${minute}`;
}

// INFO(Devs): O usuário informa data/hora local; a API recebe ISO UTC para manter o padrão atual do payload.
export const parseDateTimeLocalToUTC = (dateTimeString) => {
    if (!dateTimeString) {
        return null;
    }

    const [datePart, timePart] = dateTimeString.split('T');
    if (!datePart || !timePart) {
        return null;
    }

    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);

    return new Date(Date.UTC(year, month - 1, day, hour, minute, 0)).toISOString();
}

// Coloca uma uma data no formato brasileiro.
// IMPORTANTE: uma string "so data" (ex: "2026-07-25", como um LocalDate do backend chega
// no JSON) e interpretada pelo JS como meia-noite UTC - em fuso atras de UTC (Brasil,
// UTC-3), isso "volta" um dia na exibicao (25/07 vira 24/07). Por isso os componentes
// year/month/day sao extraidos manualmente e passados pro construtor local de Date (que
// NAO sofre esse deslocamento), em vez de deixar o parser nativo decidir.
export const formatDate = (dateString) => {
    if (!dateString) {
        return '';
    }

    const datePart = String(dateString).slice(0, 10);
    const [year, month, day] = datePart.split('-').map(Number);
    if (![year, month, day].every(Number.isFinite)) {
        return '';
    }
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('pt-BR'); // ou 'en-US' para formato dd/mm/yyyy
}

export const formatDateObj = (date) => {
    return date.toLocaleDateString('pt-BR'); // ou 'en-US' para formato dd/mm/yyyy
}

/**
 * Interpreta datas LocalDateTime do backend ("yyyy-MM-dd HH:mm:ss") no fuso local,
 * sem depender do suporte variável dos navegadores ao separador por espaço.
 */
export const parseDateTime = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    if (Array.isArray(value) && value.length >= 3) {
        const [year, month, day, hour = 0, minute = 0, second = 0, nanosecond = 0] = value;
        const date = new Date(year, month - 1, day, hour, minute, second, Math.floor(nanosecond / 1_000_000));
        return Number.isNaN(date.getTime()) ? null : date;
    }

    const dateTime = String(value).trim();
    const localDateTimeMatch = dateTime.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?)?$/);
    if (localDateTimeMatch) {
        const [, year, month, day, hour = '0', minute = '0', second = '0', fraction = '0'] = localDateTimeMatch;
        const milliseconds = Number(fraction.slice(0, 3).padEnd(3, '0'));
        const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second), milliseconds);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    const date = new Date(dateTime);
    return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateTime = (dateString) => {
    const date = parseDateTime(dateString);
    if (!date) return '';
    const options = {
        year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
        second: 'numeric'
    };
    return date.toLocaleString('pt-BR',
        options);
};

export const formatDateTimeObj = (date) => {
    const options = {
        year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
        second: 'numeric'
    };
    return date.toLocaleString('pt-BR',
        options);
};

export const formatUTCDate = (dateString) => {
    if (!dateString) {
        return '';
    }

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // +1 porque meses começam em 0
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
};

export const formatDateToYYYYMMDD = (date) => {
    // Obtém as partes da data (ano, mês e dia)
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Adiciona um zero à esquerda se necessário
    const day = date.getDate().toString().padStart(2, '0'); // Adiciona um zero à esquerda se necessário

    // Concatena as partes no formato desejado
    return `${year}-${month}-${day}`;
}

export const parseToUTCDate = (dateString) => {
    // Valida se a data não é nula ou vazia
    if (!dateString) {
        return null;
    }
    
    // Cria a data com UTC
    const [year, month, day] = dateString.split('-').map(Number);
    const dateUTC = new Date(Date.UTC(year, month - 1, day));

    // Adiciona o offset de horas
    dateUTC.setHours(dateUTC.getHours() + 3);
    return dateUTC;
};

/**
 * Extrai o ano de um date string (YYYY-mm-dd HH:mm:ss)
 */
export const extractYear = (dateString) => {
    // Check if the date string is in the expected format
    const regex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
    if (!regex.test(dateString)) {
        return null; // Return null if the format is incorrect
    }

    // Split the string into an array using the hyphen as a separator
    const dateParts = dateString.split('-');

    // The first element of the array will be the year
    const year = dateParts[0];

    return year;
}

// Retorna o ano atual.
export const currentYear = () => {
    const today = new Date();
    return today.getFullYear();
}

export const endYear = () => {
    return currentYear() + 20;
}

export const startYear = () => {
    return currentYear() - 1;
}

export const currentMonth = () => {
    const today = new Date();
    return today.getMonth() + 1;
}

export const formatMonthNumericValueToString = (value) => {
    return value < 10 ? "0" + value : value.toString()
}

const monthYearFormatter = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' });

/**
 * Mês de referência por extenso, como "Setembro de 2026", a partir do mês (1 a 12) e do ano.
 * Primeira letra maiúscula, padrão dos meses na interface. Usado no cabeçalho dos PDFs do
 * Desempenho; o portal do colaborador tem uma versão local que pode passar a usar esta.
 */
export const formatMonthYearLabel = (month, year) => {
    const formatted = monthYearFormatter.format(new Date(year, month - 1, 1));
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

export const dateTimeNow = () => {
    return new Date();
}

export const getDateRange = (month, year) => {
    month = formatMonthNumericValueToString(month);
    return {
        start: `${year}-${month}-01T00:00:00`,
        end: `${year}-${month}-${formatMonthNumericValueToString(getLastDayOfMonth(month, year))}T23:59:59`
    };
};

// Retorna o último dia do nês
export const getLastDayOfMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
}

export const parseYYYMMDDToDate = (yyyyMMdd) => {
    if (!/\d{4}-\d{2}-\d{2}/.test(yyyyMMdd)) {
        throw new Error("Formato inválido. Use 'yyyy-mm-dd'.");
    }

    const [year, month, day] = yyyyMMdd.split('-').map(Number);
    return new Date(year, month - 1, day);
}

/**
 * Retorna todos os dias de um mês.
 */
export const getAllDaysOfMonth = (month, year) => {
    const days = [];
    const lastDay = getLastDayOfMonth(month, year); // Último dia do mês
   
    for (let i = 1; i <= lastDay; i++) {
        days.push(i);
    }

    return days;
};

// Retorna o primeiro dia do mês corrente no formato YYYY-MM-DD
export const getFirstDayOfCurrentMonth = () => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

  return firstDay.toISOString().split('T')[0];
}

// Retorna o primeiro dia do mês corrente como objeto Date (para filtros que usam Date, não string).
export const getFirstDayOfCurrentMonthDate = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), 1);
}

// Retorna o último dia do mês corrente no formato YYYY-MM-DD
export const getLastDayOfCurrentMonth = () => {
  const today = new Date();
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  return lastDay.toISOString().split('T')[0];
}

/**
 * Retorna uma string ISO local (sem timezone / sem 'Z').
 * Exemplo: 2025-10-17 10:00 -> "2025-10-17T10:00:00"
 */
export const toLocalISOString = (date) => {
  if (!date) return null;

  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 19);
};

/**
 * Retorna uma string ISO completa em UTC (com timezone / com 'Z').
 * Exemplo: 2025-10-17 10:00 (SP) -> "2025-10-17T13:00:00.000Z"
 */
export const toUTCISOString = (date) => {
  if (!date) return null;
  return date.toISOString();
};

/**
 * Distância em dias de calendário até hoje: "hoje", "ontem", "há 12 dias", "há 3 meses".
 *
 * Diferente de formatTimeAgo, que mede horas corridas e serve para mensagens: aqui conta a
 * virada do dia, então uma visita de ontem às 18h é "ontem" mesmo hoje às 9h. Acima de 60 dias
 * passa a contar meses de 30 dias, que é a precisão que interessa para quem olha uma visita
 * antiga.
 */
export const formatDaysAgo = (dateString) => {
    const date = parseDateTime(dateString);
    if (!date) return '';

    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    // Math.round absorve a hora a mais ou a menos de um dia com troca de horário de verão.
    const days = Math.round((startOfToday - startOfDate) / 86400000);

    if (days <= 0) return 'hoje';
    if (days === 1) return 'ontem';
    if (days < 60) return `há ${days} dias`;
    return `há ${Math.floor(days / 30)} meses`;
};

/**
 * Formata uma data para exibição relativa (ex: "há 5 minutos", "ontem", etc.)
 */
export const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    
    const date = parseDateTime(dateString);
    if (!date) return '';
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
        return 'agora';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
        return `${diffInMinutes}min`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
        return `${diffInHours}h`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) {
        return 'ontem';
    }
    
    if (diffInDays < 7) {
        return `${diffInDays}d`;
    }
    
    // For dates older than a week, show the formatted date
    return date.toLocaleDateString('pt-BR');
};
